import type { JSX } from 'react';
import type { SurfaceOf } from './types';
import { ageFromBirthDate, flagClass, formatDate, fullName, sexLabel } from './format';
import { Markdown } from '../components/Markdown';
import { useT, useUiLanguage } from '../i18n/useT';

export function PatientSummarySurfaceView({ surface }: { surface: SurfaceOf<'patient-summary'> }): JSX.Element {
  const t = useT();
  const lang = useUiLanguage();
  const p = surface.patient;
  const age = surface.age ?? ageFromBirthDate(p.birthDate);
  return (
    <section className="surface surface-summary">
      <header className="summary-header">
        <h3 className="surface-title">{fullName(p.name, lang === 'ar')}</h3>
        <span className="muted small">
          {t('mrn')} {p.mrn} · {sexLabel(p.sex)} · {age !== undefined ? `${age}y` : ''} ({p.birthDate})
        </span>
        {p.flags.map((f) => (
          <span key={f} className="chip chip-flag">
            {f}
          </span>
        ))}
      </header>
      <div className="summary-grid">
        {surface.sections.map((s) => (
          <div key={s.title} className="summary-section">
            <h4>{s.title}</h4>
            {s.items.length === 0 ? (
              <p className="muted small">{s.emptyMessage ?? t('none')}</p>
            ) : (
              <ul>
                {s.items.map((it, i) => (
                  <li key={it.id ?? i}>
                    <span className="item-label">{it.label}</span>
                    <span className="item-value">
                      {it.value} {it.flag ? <span className={flagClass(it.flag)}>{it.flag}</span> : null}
                    </span>
                    {it.date ? <span className="muted small">{formatDate(it.date)}</span> : null}
                    {it.provenance === 'ai-narrative' ? <span className="badge badge-ai">{t('aiNarrative')}</span> : null}
                    {it.provenance === 'patient-reported' ? <span className="badge">patient-reported</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      {surface.narrative ? (
        <div className="narrative">
          <span className="badge badge-ai">{t('aiSummaryLabel')}</span>
          <Markdown text={surface.narrative} />
        </div>
      ) : null}
    </section>
  );
}
